import { dynamicBaseQuery } from "@/api/DynamicBaseQuery";
import { APIEndpoints } from "@/constants/apiEndpoints";
import type {
  IApiPaginatedMeta,
  IApiPaginatedResponse,
  IApiResponse,
} from "@/types";
import type {
  IAddLinkPayload,
  IAddTaskCommentPayload,
  IBoardColumnInput,
  IBulkCreatePreview,
  IBulkCreateResult,
  IBulkUpdateTasksPayload,
  ICreateLabelPayload,
  ICreateTaskPayload,
  IListTasksParams,
  IMoveTaskPayload,
  IReorderBacklogPayload,
  IReorderColumnsPayload,
  IBoardReports,
  IBoardSummary,
  IControlChartPoint,
  ICreatedVsResolvedPoint,
  ICreateSprintPayload,
  ICreateWorklogPayload,
  ICumulativeFlowReport,
  IEpicBurndownRow,
  ISprintCompletionSummary,
  ISprintReport,
  ISingleTask,
  ITaskAttachment,
  ITaskBoard,
  ITaskBoardColumn,
  ITaskComment,
  ITaskFilterValues,
  ITaskLabel,
  ITaskSavedFilter,
  IStartSprintPayload,
  ITaskSprint,
  ITaskImport,
  IImportWindowPreview,
  IRunTaskImportPayload,
  IStartJiraImportPayload,
  ITaskUser,
  IUpdateBoardPayload,
  IUpdateSprintPayload,
  IUpdateTaskPayload,
  IUpdateWorklogPayload,
} from "@/types/(company)/tasks.interface";
import { createApi } from "@reduxjs/toolkit/query/react";
import {
  applyOptimisticBacklogReorder,
  applyOptimisticMove,
} from "@/utils/optimistic-task-move";

const TASKS = APIEndpoints.Company.Tasks;

export const companyTasksApi = createApi({
  reducerPath: "companyTasksApi",
  baseQuery: dynamicBaseQuery,
  tagTypes: [
    "CompanyBoards",
    "CompanyBoard",
    "CompanyBoardColumns",
    "CompanyBoardLabels",
    "CompanyTasks",
    "CompanyTask",
    "CompanyTaskComments",
    "CompanySprints",
    "CompanySavedFilters",
    "CompanyBoardImports",
  ],
  endpoints: (builder) => ({
    // ── Boards (one per project; provisioned on first open) ───────────────────
    companyGetProjectBoard: builder.query<
      IApiResponse<ITaskBoard>,
      { projectId: number }
    >({
      query: ({ projectId }) => ({ url: TASKS.ProjectBoard(projectId) }),
      providesTags: ["CompanyBoard"],
    }),
    companyGetBoard: builder.query<IApiResponse<ITaskBoard>, { boardId: number }>(
      {
        query: ({ boardId }) => ({ url: TASKS.BoardById(boardId) }),
        providesTags: ["CompanyBoard"],
      },
    ),
    companyGetBoardSummary: builder.query<
      IApiResponse<IBoardSummary>,
      { boardId: number }
    >({
      query: ({ boardId }) => ({ url: TASKS.BoardSummary(boardId) }),
      providesTags: ["CompanyTasks"],
    }),
    companyGetBoardMembers: builder.query<
      IApiResponse<ITaskUser[]>,
      { boardId: number }
    >({
      query: ({ boardId }) => ({ url: TASKS.BoardMembers(boardId) }),
      providesTags: ["CompanyBoard"],
    }),
    companyUpdateBoard: builder.mutation<
      IApiResponse<ITaskBoard>,
      { boardId: number; payload: IUpdateBoardPayload }
    >({
      query: ({ boardId, payload }) => ({
        url: TASKS.BoardById(boardId),
        method: "PATCH",
        body: payload,
      }),
      invalidatesTags: ["CompanyBoard"],
    }),

    companyGetBoardReports: builder.query<
      IApiResponse<IBoardReports>,
      { boardId: number }
    >({
      query: ({ boardId }) => ({ url: TASKS.BoardReports(boardId) }),
      providesTags: ["CompanySprints", "CompanyTasks"],
    }),
    companyGetCreatedVsResolved: builder.query<
      IApiResponse<ICreatedVsResolvedPoint[]>,
      { boardId: number; weeks?: number }
    >({
      query: ({ boardId, weeks }) => ({
        url: TASKS.ReportCreatedVsResolved(boardId),
        params: weeks ? { weeks } : undefined,
      }),
      providesTags: ["CompanyTasks"],
    }),
    companyGetEpicBurndown: builder.query<
      IApiResponse<IEpicBurndownRow[]>,
      { boardId: number }
    >({
      query: ({ boardId }) => ({ url: TASKS.ReportEpicBurndown(boardId) }),
      providesTags: ["CompanyTasks"],
    }),
    companyGetControlChart: builder.query<
      IApiResponse<IControlChartPoint[]>,
      { boardId: number }
    >({
      query: ({ boardId }) => ({ url: TASKS.ReportControlChart(boardId) }),
      providesTags: ["CompanyTasks"],
    }),
    companyGetCumulativeFlow: builder.query<
      IApiResponse<ICumulativeFlowReport>,
      { boardId: number; weeks?: number }
    >({
      query: ({ boardId, weeks }) => ({
        url: TASKS.ReportCfd(boardId),
        params: weeks ? { weeks } : undefined,
      }),
      providesTags: ["CompanyTasks"],
    }),
    companyGetSprintReport: builder.query<
      IApiResponse<ISprintReport>,
      { sprintId: number }
    >({
      query: ({ sprintId }) => ({ url: TASKS.SprintReport(sprintId) }),
      providesTags: ["CompanySprints", "CompanyTasks"],
    }),

    // ── Saved filters ───────────────────────────────────────────────────────
    companyGetSavedFilters: builder.query<
      IApiResponse<ITaskSavedFilter[]>,
      { boardId: number }
    >({
      query: ({ boardId }) => ({ url: TASKS.SavedFilters(boardId) }),
      providesTags: ["CompanySavedFilters"],
    }),
    companyCreateSavedFilter: builder.mutation<
      IApiResponse<ITaskSavedFilter>,
      { boardId: number; name: string; filters: ITaskFilterValues }
    >({
      query: ({ boardId, name, filters }) => ({
        url: TASKS.SavedFilters(boardId),
        method: "POST",
        body: { name, filters },
      }),
      invalidatesTags: ["CompanySavedFilters"],
    }),
    companyDeleteSavedFilter: builder.mutation<
      IApiResponse<void>,
      { filterId: number }
    >({
      query: ({ filterId }) => ({
        url: TASKS.SavedFilterById(filterId),
        method: "DELETE",
      }),
      invalidatesTags: ["CompanySavedFilters"],
    }),

    // ── Sprints ───────────────────────────────────────────────────────────────
    companyGetSprints: builder.query<
      IApiResponse<ITaskSprint[]>,
      { boardId: number }
    >({
      query: ({ boardId }) => ({ url: TASKS.BoardSprints(boardId) }),
      providesTags: ["CompanySprints"],
    }),
    companyCreateSprint: builder.mutation<
      IApiResponse<ITaskSprint>,
      { boardId: number; payload: ICreateSprintPayload }
    >({
      query: ({ boardId, payload }) => ({
        url: TASKS.BoardSprints(boardId),
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["CompanySprints"],
    }),
    companyUpdateSprint: builder.mutation<
      IApiResponse<ITaskSprint>,
      { sprintId: number; payload: IUpdateSprintPayload }
    >({
      query: ({ sprintId, payload }) => ({
        url: TASKS.SprintById(sprintId),
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["CompanySprints"],
    }),
    companyStartSprint: builder.mutation<
      IApiResponse<ITaskSprint>,
      { sprintId: number; payload?: IStartSprintPayload }
    >({
      query: ({ sprintId, payload }) => ({
        url: TASKS.StartSprint(sprintId),
        method: "POST",
        body: payload ?? {},
      }),
      invalidatesTags: ["CompanySprints", "CompanyTasks"],
    }),
    companyGetSprintCompletionSummary: builder.query<
      IApiResponse<ISprintCompletionSummary>,
      { sprintId: number }
    >({
      query: ({ sprintId }) => ({
        url: TASKS.SprintCompletionSummary(sprintId),
      }),
    }),
    companyCompleteSprint: builder.mutation<
      IApiResponse<ITaskSprint>,
      { sprintId: number }
    >({
      query: ({ sprintId }) => ({
        url: TASKS.CompleteSprint(sprintId),
        method: "POST",
      }),
      invalidatesTags: ["CompanySprints", "CompanyTasks"],
    }),
    companyDeleteSprint: builder.mutation<
      IApiResponse<void>,
      { sprintId: number }
    >({
      query: ({ sprintId }) => ({
        url: TASKS.SprintById(sprintId),
        method: "DELETE",
      }),
      invalidatesTags: ["CompanySprints", "CompanyTasks"],
    }),
    companyAssignTaskSprint: builder.mutation<
      IApiResponse<ISingleTask>,
      { taskId: number; sprintId: number | null }
    >({
      query: ({ taskId, sprintId }) => ({
        url: TASKS.AssignTaskSprint(taskId),
        method: "PATCH",
        body: { sprintId },
      }),
      invalidatesTags: ["CompanyTasks", "CompanySprints"],
    }),

    // ── Columns ─────────────────────────────────────────────────────────────
    companyGetBoardColumns: builder.query<
      IApiResponse<ITaskBoardColumn[]>,
      { boardId: number }
    >({
      query: ({ boardId }) => ({ url: TASKS.BoardColumns(boardId) }),
      providesTags: ["CompanyBoardColumns"],
    }),
    companyAddColumn: builder.mutation<
      IApiResponse<ITaskBoardColumn>,
      { boardId: number; payload: IBoardColumnInput }
    >({
      query: ({ boardId, payload }) => ({
        url: TASKS.BoardColumns(boardId),
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["CompanyBoardColumns", "CompanyBoard"],
    }),
    companyUpdateColumn: builder.mutation<
      IApiResponse<ITaskBoardColumn>,
      { boardId: number; columnId: number; payload: Partial<IBoardColumnInput> }
    >({
      query: ({ boardId, columnId, payload }) => ({
        url: TASKS.BoardColumnById(boardId, columnId),
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["CompanyBoardColumns", "CompanyBoard"],
    }),
    companyReorderColumns: builder.mutation<
      IApiResponse<ITaskBoardColumn[]>,
      { boardId: number; payload: IReorderColumnsPayload }
    >({
      query: ({ boardId, payload }) => ({
        url: TASKS.ReorderColumns(boardId),
        method: "POST",
        body: payload,
      }),
      // Optimistically renumber the columns (server sets position = index in
      // the given order) so a dragged column settles in place instead of
      // snapping back until the refetch. Patches whichever board query is live.
      async onQueryStarted(
        { payload },
        { dispatch, queryFulfilled, getState },
      ) {
        const rank = new Map(payload.columnIds.map((id, i) => [id, i]));
        const recipe = (draft: IApiResponse<ITaskBoard>) => {
          draft.data?.columns?.forEach((c) => {
            const r = rank.get(c.id);
            if (r != null) c.position = r;
          });
        };
        const entries = companyTasksApi.util.selectInvalidatedBy(getState(), [
          { type: "CompanyBoard" },
        ]);
        const patches: { undo: () => void }[] = [];
        for (const e of entries) {
          if (e.endpointName === "companyGetProjectBoard") {
            patches.push(
              dispatch(
                companyTasksApi.util.updateQueryData(
                  "companyGetProjectBoard",
                  e.originalArgs as { projectId: number },
                  recipe,
                ),
              ),
            );
          } else if (e.endpointName === "companyGetBoard") {
            patches.push(
              dispatch(
                companyTasksApi.util.updateQueryData(
                  "companyGetBoard",
                  e.originalArgs as { boardId: number },
                  recipe,
                ),
              ),
            );
          }
        }
        try {
          await queryFulfilled;
        } catch {
          patches.forEach((p) => p.undo());
        }
      },
      invalidatesTags: ["CompanyBoardColumns", "CompanyBoard"],
    }),
    companyDeleteColumn: builder.mutation<
      IApiResponse<void>,
      { boardId: number; columnId: number }
    >({
      query: ({ boardId, columnId }) => ({
        url: TASKS.BoardColumnById(boardId, columnId),
        method: "DELETE",
      }),
      invalidatesTags: ["CompanyBoardColumns", "CompanyBoard"],
    }),

    // ── Labels ──────────────────────────────────────────────────────────────
    companyGetBoardLabels: builder.query<
      IApiResponse<ITaskLabel[]>,
      { boardId: number }
    >({
      query: ({ boardId }) => ({ url: TASKS.BoardLabels(boardId) }),
      providesTags: ["CompanyBoardLabels"],
    }),
    companyCreateLabel: builder.mutation<
      IApiResponse<ITaskLabel>,
      { boardId: number; payload: ICreateLabelPayload }
    >({
      query: ({ boardId, payload }) => ({
        url: TASKS.BoardLabels(boardId),
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["CompanyBoardLabels"],
    }),
    companyUpdateLabel: builder.mutation<
      IApiResponse<ITaskLabel>,
      { boardId: number; labelId: number; payload: Partial<ICreateLabelPayload> }
    >({
      query: ({ boardId, labelId, payload }) => ({
        url: TASKS.BoardLabelById(boardId, labelId),
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["CompanyBoardLabels", "CompanyTasks"],
    }),
    companyDeleteLabel: builder.mutation<
      IApiResponse<void>,
      { boardId: number; labelId: number }
    >({
      query: ({ boardId, labelId }) => ({
        url: TASKS.BoardLabelById(boardId, labelId),
        method: "DELETE",
      }),
      invalidatesTags: ["CompanyBoardLabels", "CompanyTasks"],
    }),

    // ── Tasks ───────────────────────────────────────────────────────────────
    companyGetTasks: builder.query<
      IApiPaginatedResponse<ISingleTask[]>,
      IListTasksParams
    >({
      query: (params) => ({ url: TASKS.Tasks, params }),
      providesTags: ["CompanyTasks"],
    }),
    // Board / backlog / roadmap / reports need EVERY issue, not one page. This
    // loops the paginated endpoint server-side (500/page, hard-capped) so nothing
    // is silently truncated. Cached as a single entry; shares the CompanyTasks tag.
    companyGetAllTasks: builder.query<
      IApiPaginatedResponse<ISingleTask[]>,
      IListTasksParams
    >({
      async queryFn(params, _api, _extra, baseQuery) {
        const PER_PAGE = 500;
        const MAX_PAGES = 40; // safety net: 20k issues
        const all: ISingleTask[] = [];
        let meta: IApiPaginatedMeta | undefined;
        for (let page = 1; page <= MAX_PAGES; page++) {
          const res = await baseQuery({
            url: TASKS.Tasks,
            params: { ...params, page, limit: PER_PAGE },
          });
          if (res.error) return { error: res.error };
          const body = res.data as IApiPaginatedResponse<ISingleTask[]>;
          all.push(...(body.data ?? []));
          meta = body.meta;
          const lastPage = body.meta?.lastPage ?? 1;
          if (page >= lastPage) break;
          if (page === MAX_PAGES) {
            console.warn(
              `[tasks] board exceeded ${MAX_PAGES * PER_PAGE} issues — list truncated`,
            );
          }
        }
        return {
          data: {
            success: true,
            statusCode: 200,
            message: "ok",
            data: all,
            meta: {
              ...(meta as IApiPaginatedMeta),
              total: all.length,
              totalRecords: all.length,
              limit: all.length,
            },
          },
        };
      },
      providesTags: ["CompanyTasks"],
    }),
    companyGetTask: builder.query<IApiResponse<ISingleTask>, { taskId: number }>({
      query: ({ taskId }) => ({ url: TASKS.TaskById(taskId) }),
      providesTags: ["CompanyTask"],
    }),
    companySearchIssues: builder.query<IApiResponse<ISingleTask[]>, { q: string }>(
      {
        query: ({ q }) => ({ url: TASKS.SearchIssues, params: { q } }),
        providesTags: ["CompanyTasks"],
      },
    ),
    companyCreateTask: builder.mutation<
      IApiResponse<ISingleTask>,
      ICreateTaskPayload
    >({
      query: (body) => ({ url: TASKS.Tasks, method: "POST", body }),
      // Invalidate the open task too — subtasks render on the parent's detail.
      invalidatesTags: ["CompanyTasks", "CompanyTask"],
    }),
    companyUpdateTask: builder.mutation<
      IApiResponse<ISingleTask>,
      { taskId: number; payload: IUpdateTaskPayload }
    >({
      query: ({ taskId, payload }) => ({
        url: TASKS.TaskById(taskId),
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["CompanyTasks", "CompanyTask"],
    }),
    companyMoveTask: builder.mutation<
      IApiResponse<ISingleTask>,
      { taskId: number; payload: IMoveTaskPayload }
    >({
      query: ({ taskId, payload }) => ({
        url: TASKS.MoveTask(taskId),
        method: "PATCH",
        body: payload,
      }),
      // Optimistically reposition the card so the board updates instantly — no
      // refetch flicker. Patches every cached task list (any search/filter args);
      // the invalidation below still reconciles with server truth afterwards.
      async onQueryStarted(
        { taskId, payload },
        { dispatch, queryFulfilled, getState },
      ) {
        const entries = companyTasksApi.util
          .selectInvalidatedBy(getState(), [{ type: "CompanyTasks" }])
          .filter(
            (e) =>
              e.endpointName === "companyGetTasks" ||
              e.endpointName === "companyGetAllTasks",
          );
        const patches = entries.map((e) =>
          dispatch(
            companyTasksApi.util.updateQueryData(
              e.endpointName as "companyGetTasks" | "companyGetAllTasks",
              e.originalArgs as IListTasksParams,
              (draft) => {
                applyOptimisticMove(draft.data, taskId, payload);
              },
            ),
          ),
        );
        try {
          await queryFulfilled;
        } catch {
          patches.forEach((p) => p.undo());
        }
      },
      invalidatesTags: ["CompanyTasks", "CompanyTask"],
    }),
    companyDeleteTask: builder.mutation<IApiResponse<void>, { taskId: number }>({
      query: ({ taskId }) => ({ url: TASKS.TaskById(taskId), method: "DELETE" }),
      invalidatesTags: ["CompanyTasks"],
    }),
    companyBulkCreateTasks: builder.mutation<
      IApiResponse<IBulkCreatePreview | IBulkCreateResult>,
      FormData
    >({
      query: (body) => ({ url: TASKS.BulkCreateTasks, method: "POST", body }),
      invalidatesTags: ["CompanyTasks", "CompanyTask", "CompanyBoardLabels"],
    }),
    companyBulkUpdateTasks: builder.mutation<
      IApiResponse<{ updated: number }>,
      IBulkUpdateTasksPayload
    >({
      query: (body) => ({ url: TASKS.BulkTasks, method: "POST", body }),
      invalidatesTags: ["CompanyTasks", "CompanyTask"],
    }),
    companyReorderBacklog: builder.mutation<
      IApiResponse<{ updated: number }>,
      { boardId: number; payload: IReorderBacklogPayload }
    >({
      query: ({ boardId, payload }) => ({
        url: TASKS.BacklogRank(boardId),
        method: "PATCH",
        body: payload,
      }),
      // Optimistically apply the new rank/bucket so the dragged issue settles
      // in place instead of snapping back to its old spot until the refetch
      // lands. Patches every cached task list; invalidation reconciles after.
      async onQueryStarted({ payload }, { dispatch, queryFulfilled, getState }) {
        const entries = companyTasksApi.util
          .selectInvalidatedBy(getState(), [{ type: "CompanyTasks" }])
          .filter(
            (e) =>
              e.endpointName === "companyGetTasks" ||
              e.endpointName === "companyGetAllTasks",
          );
        const patches = entries.map((e) =>
          dispatch(
            companyTasksApi.util.updateQueryData(
              e.endpointName as "companyGetTasks" | "companyGetAllTasks",
              e.originalArgs as IListTasksParams,
              (draft) => {
                applyOptimisticBacklogReorder(draft.data, payload);
              },
            ),
          ),
        );
        try {
          await queryFulfilled;
        } catch {
          patches.forEach((p) => p.undo());
        }
      },
      invalidatesTags: ["CompanyTasks"],
    }),

    // ── Issue links ───────────────────────────────────────────────────────────
    companyAddLink: builder.mutation<
      IApiResponse<ISingleTask>,
      { taskId: number; payload: IAddLinkPayload }
    >({
      query: ({ taskId, payload }) => ({
        url: TASKS.TaskLinks(taskId),
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["CompanyTask", "CompanyTasks"],
    }),
    companyRemoveLink: builder.mutation<
      IApiResponse<ISingleTask>,
      { taskId: number; linkId: number }
    >({
      query: ({ taskId, linkId }) => ({
        url: TASKS.TaskLinkById(taskId, linkId),
        method: "DELETE",
      }),
      invalidatesTags: ["CompanyTask", "CompanyTasks"],
    }),

    // ── Comments ────────────────────────────────────────────────────────────
    companyGetTaskComments: builder.query<
      IApiResponse<ITaskComment[]>,
      { taskId: number }
    >({
      query: ({ taskId }) => ({ url: TASKS.TaskComments(taskId) }),
      providesTags: ["CompanyTaskComments"],
    }),
    companyAddTaskComment: builder.mutation<
      IApiResponse<ITaskComment>,
      { taskId: number; payload: IAddTaskCommentPayload }
    >({
      query: ({ taskId, payload }) => ({
        url: TASKS.TaskComments(taskId),
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["CompanyTaskComments", "CompanyTask"],
    }),
    companyUpdateTaskComment: builder.mutation<
      IApiResponse<ITaskComment>,
      { taskId: number; commentId: number; payload: IAddTaskCommentPayload }
    >({
      query: ({ taskId, commentId, payload }) => ({
        url: TASKS.TaskCommentById(taskId, commentId),
        method: "PATCH",
        body: payload,
      }),
      invalidatesTags: ["CompanyTaskComments"],
    }),
    companyDeleteTaskComment: builder.mutation<
      IApiResponse<{ id: number }>,
      { taskId: number; commentId: number }
    >({
      query: ({ taskId, commentId }) => ({
        url: TASKS.TaskCommentById(taskId, commentId),
        method: "DELETE",
      }),
      invalidatesTags: ["CompanyTaskComments", "CompanyTask"],
    }),

    // ── Watchers ──────────────────────────────────────────────────────────────
    companyWatchTask: builder.mutation<IApiResponse<{ watching: boolean }>, { taskId: number }>({
      query: ({ taskId }) => ({ url: TASKS.TaskWatch(taskId), method: "POST" }),
      invalidatesTags: ["CompanyTask"],
    }),
    companyUnwatchTask: builder.mutation<IApiResponse<{ watching: boolean }>, { taskId: number }>({
      query: ({ taskId }) => ({
        url: TASKS.TaskWatch(taskId),
        method: "DELETE",
      }),
      invalidatesTags: ["CompanyTask"],
    }),

    // ── Archive ───────────────────────────────────────────────────────────────
    companyArchiveTask: builder.mutation<IApiResponse<ISingleTask>, { taskId: number }>({
      query: ({ taskId }) => ({
        url: TASKS.TaskArchive(taskId),
        method: "POST",
      }),
      invalidatesTags: ["CompanyTask", "CompanyTasks"],
    }),
    companyUnarchiveTask: builder.mutation<IApiResponse<ISingleTask>, { taskId: number }>({
      query: ({ taskId }) => ({
        url: TASKS.TaskUnarchive(taskId),
        method: "POST",
      }),
      invalidatesTags: ["CompanyTask", "CompanyTasks"],
    }),

    // ── Worklogs (time tracking) ──────────────────────────────────────────────
    companyAddWorklog: builder.mutation<
      IApiResponse<ISingleTask>,
      { taskId: number; payload: ICreateWorklogPayload }
    >({
      query: ({ taskId, payload }) => ({
        url: TASKS.TaskWorklogs(taskId),
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["CompanyTask", "CompanyTasks"],
    }),
    companyUpdateWorklog: builder.mutation<
      IApiResponse<ISingleTask>,
      { taskId: number; worklogId: number; payload: IUpdateWorklogPayload }
    >({
      query: ({ taskId, worklogId, payload }) => ({
        url: TASKS.TaskWorklogById(taskId, worklogId),
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["CompanyTask", "CompanyTasks"],
    }),
    companyDeleteWorklog: builder.mutation<
      IApiResponse<ISingleTask>,
      { taskId: number; worklogId: number }
    >({
      query: ({ taskId, worklogId }) => ({
        url: TASKS.TaskWorklogById(taskId, worklogId),
        method: "DELETE",
      }),
      invalidatesTags: ["CompanyTask", "CompanyTasks"],
    }),

    // ── Attachments (upload before task create, claimed via attachmentIds) ────
    companyUploadTaskAttachments: builder.mutation<
      IApiResponse<ITaskAttachment[]>,
      FormData
    >({
      query: (body) => ({
        url: APIEndpoints.Common.TaskAttachmentUpload,
        method: "POST",
        body,
      }),
    }),

    // ── Board imports (Jira XML migration) ────────────────────────────────────
    companyListBoardImports: builder.query<
      IApiResponse<ITaskImport[]>,
      { boardId: number }
    >({
      query: ({ boardId }) => ({ url: TASKS.BoardImports(boardId) }),
      providesTags: ["CompanyBoardImports"],
    }),
    companyGetBoardImport: builder.query<
      IApiResponse<ITaskImport>,
      { boardId: number; importId: number }
    >({
      query: ({ boardId, importId }) => ({
        url: TASKS.BoardImportById(boardId, importId),
      }),
      providesTags: ["CompanyBoardImports"],
    }),
    companyAnalyzeBoardImport: builder.mutation<
      IApiResponse<ITaskImport>,
      { boardId: number; body: FormData }
    >({
      query: ({ boardId, body }) => ({
        url: TASKS.BoardImports(boardId),
        method: "POST",
        body,
      }),
      invalidatesTags: ["CompanyBoardImports"],
    }),
    companyStartJiraBoardImport: builder.mutation<
      IApiResponse<ITaskImport>,
      { boardId: number; payload: IStartJiraImportPayload }
    >({
      query: ({ boardId, payload }) => ({
        url: `${TASKS.BoardImports(boardId)}/from-jira`,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["CompanyBoardImports"],
    }),
    companyRunBoardImport: builder.mutation<
      IApiResponse<ITaskImport>,
      { boardId: number; importId: number; payload: IRunTaskImportPayload }
    >({
      query: ({ boardId, importId, payload }) => ({
        url: TASKS.BoardImportRun(boardId, importId),
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["CompanyBoardImports"],
    }),
    companyPreviewBoardImportWindow: builder.query<
      IApiResponse<IImportWindowPreview>,
      {
        boardId: number;
        importId: number;
        createdFrom?: string;
        createdTo?: string;
      }
    >({
      query: ({ boardId, importId, createdFrom, createdTo }) => ({
        url: TASKS.BoardImportPreview(boardId, importId),
        params: {
          ...(createdFrom ? { createdFrom } : {}),
          ...(createdTo ? { createdTo } : {}),
        },
      }),
    }),
  }),
});

export const {
  // Boards
  useCompanyGetProjectBoardQuery,
  useCompanyGetBoardQuery,
  useCompanyGetBoardSummaryQuery,
  useCompanyGetBoardMembersQuery,
  useCompanyGetBoardReportsQuery,
  useCompanyGetCreatedVsResolvedQuery,
  useCompanyGetEpicBurndownQuery,
  useCompanyGetControlChartQuery,
  useCompanyGetCumulativeFlowQuery,
  useCompanyGetSprintReportQuery,
  useCompanyUpdateBoardMutation,
  // Sprints
  useCompanyGetSprintsQuery,
  useCompanyCreateSprintMutation,
  useCompanyUpdateSprintMutation,
  useCompanyStartSprintMutation,
  useCompanyGetSprintCompletionSummaryQuery,
  useCompanyCompleteSprintMutation,
  useCompanyDeleteSprintMutation,
  useCompanyAssignTaskSprintMutation,
  // Saved filters
  useCompanyGetSavedFiltersQuery,
  useCompanyCreateSavedFilterMutation,
  useCompanyDeleteSavedFilterMutation,
  // Columns
  useCompanyGetBoardColumnsQuery,
  useCompanyAddColumnMutation,
  useCompanyUpdateColumnMutation,
  useCompanyReorderColumnsMutation,
  useCompanyDeleteColumnMutation,
  // Labels
  useCompanyGetBoardLabelsQuery,
  useCompanyCreateLabelMutation,
  useCompanyUpdateLabelMutation,
  useCompanyDeleteLabelMutation,
  // Tasks
  useCompanyGetTasksQuery,
  useCompanyGetAllTasksQuery,
  useCompanyGetTaskQuery,
  useCompanySearchIssuesQuery,
  useCompanyCreateTaskMutation,
  useCompanyBulkCreateTasksMutation,
  useCompanyUpdateTaskMutation,
  useCompanyMoveTaskMutation,
  useCompanyDeleteTaskMutation,
  useCompanyBulkUpdateTasksMutation,
  useCompanyReorderBacklogMutation,
  // Issue links
  useCompanyAddLinkMutation,
  useCompanyRemoveLinkMutation,
  // Comments
  useCompanyGetTaskCommentsQuery,
  useCompanyAddTaskCommentMutation,
  useCompanyUpdateTaskCommentMutation,
  useCompanyDeleteTaskCommentMutation,

  useCompanyWatchTaskMutation,
  useCompanyUnwatchTaskMutation,
  useCompanyArchiveTaskMutation,
  useCompanyUnarchiveTaskMutation,
  // Worklogs
  useCompanyAddWorklogMutation,
  useCompanyUpdateWorklogMutation,
  useCompanyDeleteWorklogMutation,
  // Attachments
  useCompanyUploadTaskAttachmentsMutation,
  // Board imports
  useCompanyListBoardImportsQuery,
  useCompanyGetBoardImportQuery,
  useCompanyAnalyzeBoardImportMutation,
  useCompanyStartJiraBoardImportMutation,
  useCompanyRunBoardImportMutation,
  useCompanyPreviewBoardImportWindowQuery,
} = companyTasksApi;
